'use client';

/**
 * Compact, neutral testimonial cards. Brand-tinted left accent bar,
 * decorative quote glyph top-right, avatar + author + role + index in
 * the header, blockquote body, optional "read more" link.
 * Ported from ThemeMaker's {@code blocks/proof/Testimonials}.
 *
 * <p>Card grid is responsive auto-fit; dark mode via {@code [data-theme]}
 * /{@code .dark} selector. Hover lift + accent border change unless
 * {@code prefers-reduced-motion}.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@akademi/ui';
import { tx, asList } from '../_shared/coerce';
import { sectionInner, sectionPad, sectionTitle } from '../_shared/styles';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

interface Testimonial {
  quote: Localized;
  author: Localized;
  role?: Localized;
  avatar?: string;
  href?: string;
}

export interface TestimonialsProps {
  title?: Localized;
  items?: Testimonial[];
  /** Localized "Read more" label for the link affordance. Default "Read more". */
  readMoreLabel?: Localized;
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

type CardVars = CSSProperties & {
  '--testimonial-index': string;
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

export function Testimonials({
  title,
  items,
  readMoreLabel,
  style,
  paddingOuter,
  paddingInner,
}: TestimonialsProps) {
  const { t } = useTranslation('site-builder-blocks');
  const heading = title ?? t('testimonials.title');
  const readMore = readMoreLabel ?? t('testimonials.readMore');
  const list = asList<Testimonial>(items);
  return (
    <BlockRoot
      as="section"
      className="testimonials-section"
      aria-labelledby="testimonials-title"
      style={{ ...sectionPad, ...resolvePaddingStyle(paddingOuter), ...style }}
    >
      <div style={{ ...sectionInner, ...resolvePaddingStyle(paddingInner) }}>
        <div className="testimonials-header">
          <h2 id="testimonials-title" style={sectionTitle}>
            {tx(heading)}
          </h2>
          <div className="testimonials-header-line" aria-hidden="true" />
        </div>

        {list.length === 0 ? (
          <div className="testimonials-empty">
            <EmptyState title={t('testimonials.empty.title')} hint={t('testimonials.empty.hint')} />
          </div>
        ) : (
          <div className="testimonials-grid">
            {list.map((t, index) => (
              <TestimonialCard
                key={`${tx(t.author)}-${index}`}
                testimonial={t}
                index={index}
                readMoreLabel={tx(readMore)}
              />
            ))}
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{ __html: testimonialsCss }} />
    </BlockRoot>
  );
}

function TestimonialCard({
  testimonial,
  index,
  readMoreLabel,
}: {
  testimonial: Testimonial;
  index: number;
  readMoreLabel: string;
}) {
  const quote = tx(testimonial.quote);
  const author = tx(testimonial.author);
  const role = testimonial.role ? tx(testimonial.role) : '';
  const initials = getInitials(author);
  const indexStr = String(index + 1).padStart(2, '0');
  const style: CardVars = { '--testimonial-index': indexStr };

  const content = (
    <>
      <div className="testimonial-top">
        <div className="testimonial-avatar" aria-hidden="true">
          {testimonial.avatar ? (
            <img
              src={testimonial.avatar}
              alt=""
              loading="lazy"
              decoding="async"
              width={40}
              height={40}
            />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <div className="testimonial-person">
          <cite className="testimonial-author">{author}</cite>
          {role && <div className="testimonial-role">{role}</div>}
        </div>
        <div className="testimonial-number" aria-hidden="true">
          {indexStr}
        </div>
      </div>

      <blockquote className="testimonial-quote">
        <p>“{quote}”</p>
      </blockquote>

      {testimonial.href && (
        <div className="testimonial-link-text" aria-hidden="true">
          {readMoreLabel}
          <span aria-hidden="true">↗</span>
        </div>
      )}
    </>
  );

  if (testimonial.href) {
    return (
      <Card asChild>
        <a
          href={testimonial.href}
          className="testimonial-card testimonial-card-link"
          style={style}
          aria-label={`${author}${role ? `, ${role}` : ''}: ${quote}`}
        >
          {content}
        </a>
      </Card>
    );
  }

  return (
    <Card asChild>
      <article className="testimonial-card" style={style}>
        {content}
      </article>
    </Card>
  );
}

const testimonialsCss = `
.testimonials-section { background: transparent; }
.testimonials-header {
  display: flex; align-items: center;
  gap: var(--space-5, 20px);
}
.testimonials-header-line {
  flex: 1; height: 1px;
  background: linear-gradient(to right,
    var(--color-border-subtle, rgba(120, 120, 140, 0.24)), transparent);
}
.testimonials-empty { margin-top: var(--space-6, 24px); }
.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: var(--space-5, 20px);
  margin-top: var(--space-7, 28px);
}
/* Rendered as <Card>: border + elevation come from the component
   (ring-(--color-border-brand) + shadow-(--shadow-brand)). */
.testimonial-card {
  position: relative;
  display: flex; flex-direction: column;
  min-height: 192px;
  padding: var(--space-5, 20px);
  border-radius: var(--radius-lg, 16px);
  background: linear-gradient(180deg,
    color-mix(in srgb, var(--color-surface-card, #ffffff) 96%, white 4%),
    var(--color-surface-card, #ffffff));
  color: var(--color-text-primary, #18181b);
  text-decoration: none;
  overflow: hidden;
  transition: transform 180ms ease, background 180ms ease;
}
.testimonial-card::before {
  content: ""; position: absolute;
  left: 0;
  top: var(--space-5, 20px); bottom: var(--space-5, 20px);
  width: 3px;
  border-radius: var(--radius-pill, 9999px);
  background: var(--color-brand-500, #3b82f6);
  opacity: 0.45;
}
.testimonial-card::after {
  content: "\\201C"; position: absolute;
  top: var(--space-2, 8px); right: var(--space-4, 16px);
  font-family: var(--font-family-display, Georgia, 'Times New Roman', serif);
  font-size: 64px; line-height: 1;
  color: var(--color-brand-500, #3b82f6);
  opacity: 0.08;
  pointer-events: none; user-select: none;
}
.testimonial-card-link { cursor: pointer; }
.testimonial-card-link:focus { outline: none; }
.testimonial-card-link:focus-visible {
  outline: 2px solid var(--color-brand-500, #3b82f6);
  outline-offset: 4px;
}
.testimonial-card:hover,
.testimonial-card:focus-visible {
  transform: translateY(-3px);
}
.testimonial-top {
  display: flex; align-items: center;
  gap: var(--space-3, 12px); min-width: 0;
}
.testimonial-avatar {
  flex: 0 0 auto; width: 42px; height: 42px;
  border-radius: var(--radius-md, 12px);
  display: grid; place-items: center;
  overflow: hidden;
  background: linear-gradient(145deg,
    var(--color-surface-elevated, #f4f4f5),
    var(--color-surface-muted, #e4e4e7));
  color: var(--color-text-primary, #18181b);
  border: 1px solid var(--color-border-subtle, rgba(120, 120, 140, 0.24));
}
.testimonial-avatar img {
  width: 100%; height: 100%; object-fit: cover;
}
.testimonial-avatar span {
  font-family: var(--font-family-display, inherit);
  font-size: var(--font-size-sm, 14px);
  font-weight: 700;
  letter-spacing: var(--font-tracking-snug, -0.02em);
}
.testimonial-person { min-width: 0; flex: 1; }
.testimonial-author {
  display: block;
  font-family: var(--font-family-display, inherit);
  color: var(--color-text-primary, #18181b);
  font-style: normal;
  font-size: var(--font-size-sm, 14px);
  font-weight: 700;
  line-height: 1.25;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.testimonial-role {
  margin-top: 2px;
  color: var(--color-text-secondary, #71717a);
  font-size: var(--font-size-xs, 12px);
  line-height: 1.3;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.testimonial-number {
  flex: 0 0 auto;
  color: var(--color-text-tertiary, #a1a1aa);
  font-family: var(--font-family-body, inherit);
  font-size: var(--font-size-xs, 12px);
  font-weight: 600; letter-spacing: 0.04em;
}
.testimonial-quote {
  margin: var(--space-4, 16px) 0 0;
  position: relative; z-index: 1;
}
.testimonial-quote p {
  margin: 0;
  font-family: var(--font-family-body, inherit);
  color: var(--color-text-primary, #18181b);
  font-size: var(--font-size-md, 16px);
  font-weight: 400; line-height: 1.55;
  letter-spacing: var(--font-tracking-snug, -0.01em);
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.testimonial-link-text {
  margin-top: auto;
  padding-top: var(--space-4, 16px);
  display: inline-flex; align-items: center;
  gap: 6px; width: fit-content;
  font-family: var(--font-family-display, inherit);
  color: var(--color-text-secondary, #71717a);
  font-size: var(--font-size-xs, 12px);
  font-weight: 600; letter-spacing: 0.02em;
  transition: color 180ms ease, gap 180ms ease;
}
.testimonial-link-text span {
  font-size: var(--font-size-xs, 12px);
  transform: translateY(-1px);
  transition: transform 180ms ease;
}
.testimonial-card-link:hover .testimonial-link-text,
.testimonial-card-link:focus-visible .testimonial-link-text {
  color: var(--color-brand-500, #3b82f6);
  gap: 8px;
}
.testimonial-card-link:hover .testimonial-link-text span {
  transform: translate(1px, -2px);
}
:where([data-theme="dark"], .dark) .testimonial-card {
  background: linear-gradient(180deg,
    color-mix(in srgb, var(--color-surface-card, #18181b) 94%, white 6%),
    var(--color-surface-card, #18181b));
}
:where([data-theme="dark"], .dark) .testimonial-card::after { opacity: 0.12; }
:where([data-theme="dark"], .dark) .testimonial-avatar {
  background: linear-gradient(145deg,
    var(--color-surface-elevated, #27272a),
    var(--color-surface-muted, #18181b));
  border-color: var(--color-border-subtle, rgba(255, 255, 255, 0.1));
}
@media (max-width: 680px) {
  .testimonials-header-line { display: none; }
  .testimonial-card { min-height: auto; }
  .testimonial-card::after { font-size: 56px; }
}
@media (max-width: 480px) {
  .testimonial-card {
    padding: var(--space-4, 16px);
    border-radius: var(--radius-md, 12px);
  }
  .testimonial-card::before {
    top: var(--space-4, 16px);
    bottom: var(--space-4, 16px);
  }
  .testimonial-quote p { -webkit-line-clamp: 5; }
}
@media (hover: none) {
  .testimonial-card:hover { transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .testimonial-card,
  .testimonial-link-text,
  .testimonial-link-text span { transition: none !important; }
  .testimonial-card:hover,
  .testimonial-card:focus-visible { transform: none !important; }
}
`;
